/**
 * @file BlockedCountriesSection.tsx
 * @brief Super Admin → Billing → Plans: the countries that can't buy plans
 *
 * @details
 * DES 22-10-2026: every country can buy plans (personal and Business)
 * except this block-list; it replaces the old "Allowed countries" picker
 * (`SUBSCRIPTION_COUNTRIES`, no longer read). Contract: BACKEND B1,
 * GET/PUT /api/admin/billing-countries. No saved row = the defaults (EU 27
 * + EEA + sanctions) are in force; the first Save writes the row.
 *
 * Its own Save, apart from the plan prices above it: the list is edited
 * here, "Will change: + AF, − FR" shows first, then the WHOLE list is sent
 * with the optional reason (activity log).
 *
 * @author DES
 * @date 22-10-2026
 */

import { useEffect, useMemo, useState } from "react";
import { RotateCcw, Search, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { CountryFlag } from "@/components/ui/CountryFlag";
import { CountryPicker } from "@/components/ui/CountryPicker";
import { FormFieldGroup } from "@/components/ui/FormDialog";
import { Input } from "@/components/ui/Input";
import { SkeletonFields } from "@/components/ui/Skeleton";
import { GridBand, GridCell } from "@/features/analytics/components/GridBlock";
import { ApiClientError } from "@/shared/api/client";
import { findCountry } from "@/shared/data/countries";
import { useBillingCountries, useUpdateBillingCountries } from "../api";
import {
  changeLine,
  diffBlocked,
  hasChanges,
  normalizeCodes,
  openLine,
} from "../blockedCountries";

const REASON_MAX = 200;

export function BlockedCountriesSection() {
  const query = useBillingCountries();
  const update = useUpdateBillingCountries();
  const data = query.data;

  // Names from the API (English, Intl); a code added here takes ours.
  const names = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of [...(data?.defaults ?? []), ...(data?.blocked ?? [])])
      m.set(c.code.toUpperCase(), c.name);
    return m;
  }, [data]);
  const nameOf = (code: string) =>
    names.get(code) ?? findCountry(code)?.name ?? code;

  const baseline = useMemo(
    () => normalizeCodes((data?.blocked ?? []).map((c) => c.code)),
    [data],
  );
  const [codes, setCodes] = useState<string[]>(baseline);
  const [reason, setReason] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => setCodes(baseline), [baseline]);

  const diff = useMemo(() => diffBlocked(baseline, codes), [baseline, codes]);
  const changed = hasChanges(diff);

  const sorted = useMemo(
    () => [...codes].sort((a, b) => nameOf(a).localeCompare(nameOf(b))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [codes, names],
  );
  const needle = search.trim().toLowerCase();
  const shown = needle
    ? sorted.filter(
        (c) =>
          c.toLowerCase().includes(needle) ||
          nameOf(c).toLowerCase().includes(needle),
      )
    : sorted;

  const add = (countryName: string | null) => {
    const match = countryName ? findCountry(countryName) : null;
    if (!match) return;
    update.reset();
    setCodes((prev) => normalizeCodes([...prev, match.code]));
  };
  const remove = (code: string) => {
    update.reset();
    setCodes((prev) => prev.filter((c) => c !== code));
  };
  const undo = () => {
    update.reset();
    setCodes(baseline);
    setReason("");
  };

  const onSave = () => {
    if (!changed) return;
    const why = reason.trim();
    update.mutate(
      { blocked: codes, ...(why ? { reason: why } : {}) },
      { onSuccess: () => setReason("") },
    );
  };

  const saveError =
    update.error instanceof ApiClientError &&
    update.error.code === "INVALID_COUNTRY_CODE"
      ? update.error.message
      : update.error
        ? `Save failed: ${update.error.message}`
        : null;

  return (
    <>
      <GridBand className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h3 className="text-sm font-semibold text-foreground">
            Blocked countries
          </h3>
          <p className="text-xs text-muted-foreground">
            Every country can buy plans, personal and Business, except these.
            Pay as you go is not affected. Saved on its own, apart from the
            prices on this page.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {update.isSuccess && !changed ? (
            <p className="text-sm text-muted-foreground">Saved.</p>
          ) : null}
          {changed ? (
            <Button variant="ghost" size="sm" onClick={undo}>
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              Undo
            </Button>
          ) : null}
          <Button
            size="sm"
            onClick={onSave}
            disabled={!data || !changed || update.isPending}
          >
            {update.isPending
              ? "Saving…"
              : changed
                ? "Save blocked countries"
                : "No changes"}
          </Button>
        </div>
      </GridBand>

      {query.isLoading ? (
        <GridCell>
          <SkeletonFields fields={2} />
        </GridCell>
      ) : query.isError || !data ? (
        <GridCell>
          <p className="text-sm text-destructive">
            Couldn't load the blocked countries.
            {query.error ? ` ${query.error.message}` : ""}
          </p>
        </GridCell>
      ) : (
        <>
          <GridCell className="space-y-1.5">
            <p className="text-sm font-medium">{openLine(codes.length)}</p>
            {!data.saved ? (
              <p className="text-xs text-muted-foreground">
                This is the default list (EU 27, EEA and sanctioned countries).
                It is saved the first time you press Save.
              </p>
            ) : null}
          </GridCell>

          {changed ? (
            <GridCell className="space-y-3">
              <p className="text-sm">
                <span className="font-semibold">Will change: </span>
                <span className="font-mono">{changeLine(diff)}</span>
              </p>
              <ul className="space-y-0.5 text-xs text-muted-foreground">
                {diff.added.map((c) => (
                  <li key={`+${c}`}>Block {nameOf(c)}</li>
                ))}
                {diff.removed.map((c) => (
                  <li key={`-${c}`}>Open {nameOf(c)}</li>
                ))}
              </ul>
              <FormFieldGroup
                label="Reason (optional)"
                hint="Kept in the activity log with the change."
              >
                <Input
                  value={reason}
                  maxLength={REASON_MAX}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. New sanctions list"
                />
              </FormFieldGroup>
            </GridCell>
          ) : null}

          {saveError ? (
            <GridCell>
              <p className="text-sm text-destructive">{saveError}</p>
            </GridCell>
          ) : null}

          <GridCell className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search blocked countries"
                  className="pl-9"
                  aria-label="Search blocked countries"
                />
              </div>
              <CountryPicker
                value={null}
                onChange={add}
                placeholder="Block a country…"
                sheetTitle="Block a country"
                sheetSubtitle="Search by name or ISO code. People there can't buy plans."
                size="sm"
              />
            </div>

            {codes.length === 0 ? (
              <p className="py-1 text-sm text-muted-foreground">
                No country is blocked.
              </p>
            ) : shown.length === 0 ? (
              <p className="py-1 text-sm text-muted-foreground">
                No blocked country matches “{search.trim()}”.
              </p>
            ) : (
              <ul className="grid grid-cols-1 gap-x-4 sm:grid-cols-2 lg:grid-cols-3">
                {shown.map((code) => {
                  const isNew = diff.added.includes(code);
                  return (
                    <li
                      key={code}
                      className="flex min-w-0 items-center gap-2 border-b border-border/60 py-1.5"
                    >
                      <CountryFlag code={code} size="sm" />
                      <span className="min-w-0 truncate text-sm">
                        {nameOf(code)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {code}
                      </span>
                      {isNew ? (
                        <span className="text-xs font-medium text-primary">
                          New
                        </span>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => remove(code)}
                        className="ml-auto rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        title={`Unblock ${nameOf(code)}`}
                        aria-label={`Unblock ${nameOf(code)}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </GridCell>
        </>
      )}
    </>
  );
}
